/**
 * DEVLOGS-TEMP — folding high-frequency events into one decision-dense record.
 *
 * `timeupdate` fires ~4/s per media element, and up to four elements are live at
 * once (two audio for crossfade, foreground video, signage video) — ~16/s,
 * ~57,000/hour, per device.
 *
 * Sampling would be the obvious answer and it is the wrong one: a sampled tick
 * tells you a POSITION. What the stall bug needs is whether time ADVANCED, which
 * is a property of the window, not of any tick. `rate ~= 0 while !paused &&
 * readyState >= 2` is exactly the stall signature that use-playback.tsx
 * currently detects with a throwaway 2.5s timer — and here it becomes one
 * queryable row instead of 1,200 discarded ones.
 */

export interface ProgressSummary {
  ticks: number;
  /** Media currentTime at window open / close, seconds. */
  tStart: number;
  tEnd: number;
  /** tEnd - tStart. Negative after a seek backwards — deliberately NOT clamped. */
  advanced: number;
  wallMs: number;
  /** advanced / (wallMs/1000). ~1 is healthy, ~0 while playing is a stall. */
  rate: number;
}

export class TimeupdateCoalescer {
  readonly #windowMs: number;
  readonly #now: () => number;
  #ticks = 0;
  #tStart = 0;
  #tEnd = 0;
  #openedAt = 0;
  #open = false;

  constructor(windowMs: number, now: () => number = Date.now) {
    this.#windowMs = Math.max(250, windowMs);
    this.#now = now;
  }

  /** Returns a summary only when the window closes; otherwise null. */
  tick(currentTime: number, now = this.#now()): ProgressSummary | null {
    if (!this.#open) {
      this.#open = true;
      this.#openedAt = now;
      this.#tStart = currentTime;
      this.#tEnd = currentTime;
      this.#ticks = 1;
      return null;
    }
    this.#ticks += 1;
    this.#tEnd = currentTime;
    if (now - this.#openedAt >= this.#windowMs) return this.#close(now);
    return null;
  }

  /** Force the window closed — any other media event should call this first. */
  flush(now = this.#now()): ProgressSummary | null {
    if (!this.#open || this.#ticks === 0) return null;
    return this.#close(now);
  }

  #close(now: number): ProgressSummary {
    const wallMs = Math.max(0, now - this.#openedAt);
    const advanced = this.#tEnd - this.#tStart;
    const summary: ProgressSummary = {
      ticks: this.#ticks,
      tStart: round3(this.#tStart),
      tEnd: round3(this.#tEnd),
      advanced: round3(advanced),
      wallMs,
      rate: wallMs > 0 ? round3(advanced / (wallMs / 1000)) : 0,
    };
    this.#open = false;
    this.#ticks = 0;
    return summary;
  }
}

export interface FrameSummary {
  frames: number;
  bytes: number;
  wallMs: number;
}

/**
 * Folds a burst of same-kind frames (live-broadcast binary audio runs ~50/s)
 * into one record per window. Contents are never retained — only counts.
 */
export class FrameCoalescer {
  readonly #windowMs: number;
  readonly #now: () => number;
  #frames = 0;
  #bytes = 0;
  #openedAt = 0;
  #open = false;

  constructor(windowMs: number, now: () => number = Date.now) {
    this.#windowMs = Math.max(250, windowMs);
    this.#now = now;
  }

  add(bytes: number, now = this.#now()): FrameSummary | null {
    if (!this.#open) {
      this.#open = true;
      this.#openedAt = now;
      this.#frames = 0;
      this.#bytes = 0;
    }
    this.#frames += 1;
    this.#bytes += Number.isFinite(bytes) && bytes > 0 ? bytes : 0;
    if (now - this.#openedAt >= this.#windowMs) return this.#close(now);
    return null;
  }

  flush(now = this.#now()): FrameSummary | null {
    if (!this.#open || this.#frames === 0) return null;
    return this.#close(now);
  }

  #close(now: number): FrameSummary {
    const summary: FrameSummary = {
      frames: this.#frames,
      bytes: this.#bytes,
      wallMs: Math.max(0, now - this.#openedAt),
    };
    this.#open = false;
    this.#frames = 0;
    this.#bytes = 0;
    return summary;
  }
}

function round3(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.round(value * 1000) / 1000;
}
